import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthHeadersSync } from '../../shared/utils/authHelpers';

// Rule interfaces
export interface RuleNode {
  id: string;
  type: 'trigger' | 'condition' | 'action';
  data: {
    label: string;
    config: any;
  };
  position: {
    x: number;
    y: number;
  };
}

export interface RuleEdge {
  id: string;
  source: string;
  target: string;
  animated?: boolean;
  label?: string;
}

export interface Rule {
  id?: string;
  name: string;
  description: string;
  enabled: boolean;
  severity: string;
  nodes: RuleNode[];
  edges: RuleEdge[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  lastModifiedBy?: string;
  matchCount?: number;
  averageExecutionTime?: number;
  deploymentStatus?: 'draft' | 'testing' | 'production';
}

export interface RuleMetrics {
  totalRules: number;
  enabledRules: number;
  disabledRules: number;
  productionRules: number;
  averageExecutionTime: number;
  topMatchingRules: { id: string; name: string; matchCount: number }[];
}

export interface RuleFilters {
  search: string;
  status: string;
}

interface RuleState {
  rules: Rule[];
  selectedRule: Rule | null;
  currentRule: Rule | null;
  loading: boolean;
  error: string | null;
  metrics: RuleMetrics | null;
  filters: {
    enabled?: boolean;
    severity?: string;
    deploymentStatus?: string;
    search?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  validationErrors: Record<string, any>;
  isValidating: boolean;
}

const initialState: RuleState = {
  rules: [],
  selectedRule: null,
  currentRule: null,
  loading: false,
  error: null,
  metrics: null,
  filters: {
    enabled: undefined,
    severity: undefined,
    deploymentStatus: undefined,
    search: undefined
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0
  },
  validationErrors: {},
  isValidating: false
};

// Async thunks
export const fetchRules = createAsyncThunk(
  'rules/fetchRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<Rule[]>('/api/normalization-rules', {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch rules');
    }
  }
);

export const fetchRuleMetrics = createAsyncThunk(
  'rules/fetchRuleMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<RuleMetrics>('/api/normalization-rules/metrics', {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch rule metrics');
    }
  }
);

export const fetchRuleById = createAsyncThunk(
  'rules/fetchRuleById',
  async (ruleId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<Rule>(`/api/normalization-rules/${ruleId}`, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to validate rule');
    }
  }
);

export const createRule = createAsyncThunk(
  'rules/createRule',
  async (rule: Rule, { rejectWithValue }) => {
    try {
      const response = await axios.post<Rule>('/api/normalization-rules', rule, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to create rule');
    }
  }
);

export const updateRule = createAsyncThunk(
  'rules/updateRule',
  async (rule: Rule, { rejectWithValue }) => {
    try {
      if (!rule.id) {
        return rejectWithValue('Rule ID is required for update');
      }
      const response = await axios.put<Rule>(`/api/normalization-rules/${rule.id}`, rule);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to update rule');
    }
  }
);

export const deleteRule = createAsyncThunk(
  'rules/deleteRule',
  async (ruleId: string, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/normalization-rules/${ruleId}`);
      return ruleId;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to delete rule');
    }
  }
);

export const toggleRuleStatus = createAsyncThunk(
  'rules/toggleRuleStatus',
  async (ruleId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { rules: RuleState };
      const rule = state.rules.rules.find(r => r.id === ruleId) as Rule;
      
      if (!rule) {
        return rejectWithValue('Rule not found');
      }
      
      const response = await axios.patch<Rule>(`/api/normalization-rules/${ruleId}/status`, {
        enabled: !rule.enabled
      });
      
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue((error as any)?.response?.data?.message || 'Failed to toggle rule status');
    }
  }
);

export const deployRule = createAsyncThunk(
  'rules/deployRule',
  async ({ ruleId, environment }: { ruleId: string; environment: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post<Rule>(`/api/normalization-rules/${ruleId}/deploy`, {
        environment
      }, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to deploy rule');
    }
  }
);

export const testRule = createAsyncThunk(
  'rules/testRule',
  async (rule: Rule, { rejectWithValue }) => {
    try {
      const response = await axios.post<any>('/api/normalization-rules/test', rule, {
        headers: getAuthHeadersSync()
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to test rule');
    }
  }
);

// Rule slice
const ruleSlice = createSlice({
  name: 'rules',
  initialState,
  reducers: {
    setRuleFilters: (state, action: PayloadAction<RuleFilters>) => {
      state.filters = { ...action.payload } as unknown as Record<string, unknown>;
    },
    resetCurrentRule: (state) => {
      state.currentRule = null;
    },
    updateCurrentRule: (state, action: PayloadAction<Partial<Rule>>) => {
      if (state.currentRule) {
        state.metrics = { ...state.metrics, ...action.payload };
      }
    },
    updateRuleNodes: (state, action: PayloadAction<RuleNode[]>) => {
      if (state.currentRule) {
        state.currentRule.nodes = action.payload;
      }
    },
    updateRuleEdges: (state, action: PayloadAction<RuleEdge[]>) => {
      if (state.currentRule) {
        state.currentRule.edges = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch rules
    builder.addCase(fetchRules.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRules.fulfilled, (state, action: PayloadAction<Rule[]>) => {
      state.loading = false;
      state.rules = action.payload;
    });
    builder.addCase(fetchRules.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Fetch rule metrics
    builder.addCase(fetchRuleMetrics.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRuleMetrics.fulfilled, (state, action: PayloadAction<RuleMetrics>) => {
      state.loading = false;
      state.metrics = action.payload;
    });
    builder.addCase(fetchRuleMetrics.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Fetch rule by ID
    builder.addCase(fetchRuleById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRuleById.fulfilled, (state, action: PayloadAction<Rule>) => {
      state.loading = false;
      state.currentRule = action.payload;
    });
    builder.addCase(fetchRuleById.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Create rule
    builder.addCase(createRule.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createRule.fulfilled, (state, action: PayloadAction<Rule>) => {
      state.loading = false;
      state.rules.push(action.payload);
      state.currentRule = action.payload;
    });
    builder.addCase(createRule.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Update rule
    builder.addCase(updateRule.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateRule.fulfilled, (state, action: PayloadAction<Rule>) => {
      state.loading = false;
      state.rules = state.rules.map(rule => 
        rule.id === action.payload.id ? action.payload : rule
      );
      state.currentRule = action.payload;
    });
    builder.addCase(updateRule.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Delete rule
    builder.addCase(deleteRule.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteRule.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.rules = state.rules.filter(rule => rule.id !== action.payload);
      if (state.currentRule && state.currentRule.id === action.payload) {
        state.currentRule = null;
      }
    });
    builder.addCase(deleteRule.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Toggle rule status
    builder.addCase(toggleRuleStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(toggleRuleStatus.fulfilled, (state, action: PayloadAction<Rule>) => {
      state.loading = false;
      state.rules = state.rules.map(rule => 
        rule.id === action.payload.id ? action.payload : rule
      );
      if (state.currentRule && state.currentRule.id === action.payload.id) {
        state.currentRule = action.payload;
      }
    });
    builder.addCase(toggleRuleStatus.rejected, (state, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Deploy rule
    builder.addCase(deployRule.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deployRule.fulfilled, (state, action: PayloadAction<Rule>) => {
      state.loading = false;
      state.rules = state.rules.map(rule => 
        rule.id === action.payload.id ? action.payload : rule
      );
      if (state.currentRule && state.currentRule.id === action.payload.id) {
        state.currentRule = action.payload;
      }
    });
    builder.addCase(deployRule.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Test rule
    builder.addCase(testRule.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(testRule.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(testRule.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  }
});

export const { 
  setRuleFilters, 
  resetCurrentRule, 
  updateCurrentRule,
  updateRuleNodes,
  updateRuleEdges
} = ruleSlice.actions;

export default ruleSlice.reducer;
